import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { controlToastState, openModal } from "risa-oasis-ui_v2";
import { getAuthGridSource } from "../../../api/postCall/getAuthGridSource";
import AnchoredPortal from "../../../components/anchoredPortal/anchoredPortal";
import DocViewerWithSideNavigation from "../../../components/modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import ImageviewerModal from "../../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerTimestamp from "../../../components/modals/imageViewerModal/imageViewerTimestamp";
import NarGridModal from "../../../components/modals/narGridModal/narGridModal";
import PopOverWithClickableOptions from "../../../components/popOverWithClickableOptions/popOverWithClickableOptions";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { Item } from "../../../data-model/documentModalWithSideBav";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { ModalId } from "../../../enums/modalId";
import { UseHealthCareFacilityData } from "../../../hooks/useHealthCareFacility";
import {
  closeModal,
  setMetaData,
  setOpenedModalId,
} from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { ThreeDotIcon } from "../../../svg/three-dot-icon";
import { usePrimaryIcdCodes } from "../../../utils/getPrimaryDiagnosisCodes";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { formatJcodeIcd } from "../../singleOrderLayout/utils/getJCode";
import { handleViewNccnGuidelines } from "./utils/handleViewNccnGuidelines";

const optionsEnum = {
  VIEW_GUIDELINES: "view_guidelines",
  VIEW_TX_PLAN: "view_tx_plan",
  VIEW_IN_ONCO_EMR: "view_in_onco_emr",
  VIEW_DX_CODES: "view_dx_codes",
  VIEW_NAR_GRID: "view_nar_grid",
};

const ThreeDotsWithClickableOptions = (props: {
  items: Item[];
  healthCareFacilityData: UseHealthCareFacilityData;
}) => {
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const jcode_icd = useMemo(
    () => formatJcodeIcd(singleMedicalPaOrder || ({} as MedicalPaOrder)),
    [singleMedicalPaOrder],
  );
  const primaryIcdCodes = usePrimaryIcdCodes();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const orgId = getOrgIdForFetchExternalWorklist();
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const [isOpen, setIsOpen] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    right: number;
    bottom: number;
    left: number;
    width: number;
    height: number;
    x: number;
    y: number;
  }>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });

  const shouldShowViewGuidelinesOption = useMemo(() => {
    if (!props.items || props.items.length === 0) {
      return false;
    }

    // Check if there's at least one document across all items
    return props.items.some(
      (item) => item?.documents && item.documents.length > 0,
    );
  }, [props.items]);

  useEffect(() => {
    if (parentRef.current) {
      setPosition(parentRef.current.getBoundingClientRect());
    }
  }, [parentRef]);

  useEffect(() => {
    if (openedModalId === ModalId.CLINICAL_DATA_TABLE) {
      openModal(ModalId.CLINICAL_DATA_TABLE);
    }
    if (openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION) {
      openModal(ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION);
    }
    if (openedModalId === ModalId.IMAGE_VIEWER_MODAL) {
      openModal(ModalId.IMAGE_VIEWER_MODAL);
    }
    if (openedModalId === ModalId.NAR_GRID_MODAL) {
      openModal(ModalId.NAR_GRID_MODAL);
    }
  }, [openedModalId]);

  // Cleanup: abort any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      dispatch(closeModal());
    };
  }, [dispatch]);

  const handleOnOptionsClick = (id: string) => {
    switch (id) {
      case optionsEnum.VIEW_GUIDELINES:
        handleViewGuidelines();
        break;
      case optionsEnum.VIEW_TX_PLAN:
        handleViewTxPlan();
        break;
      case optionsEnum.VIEW_IN_ONCO_EMR:
        handleViewInOncoEmr();
        break;
      case optionsEnum.VIEW_DX_CODES:
        handleViewDxCodes();
        break;
      case optionsEnum.VIEW_NAR_GRID:
        handleViewNarGrid();
        break;
    }
  };
  const handleViewInOncoEmr = () => {
    if (
      !props.healthCareFacilityData.data?.onco_emr_base_url ||
      props.healthCareFacilityData.loading
    ) {
      controlToastState("onco-emr-base-url-not-found");
      return;
    }
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.VIEW_IN_ONCO_EMR_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        payer_name: payerName,
      },
    });
    const patientIdFromDb =
      singleMedicalPaOrder?.demographics?.patient_fhir_identifier ?? "";
    const finalPatientId = patientIdFromDb.split(".")[0].replaceAll("--", "_");
    const finalUrl = `${props.healthCareFacilityData.data?.onco_emr_base_url ?? ""}/nav/demographics?PID=${finalPatientId}&locationId=LH_0673T0AXW8EFAE0WHC2E`;
    window.open(finalUrl, "_blank");
  };

  const handleViewDxCodes = () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.VIEW_DX_CODES_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        payer_name: payerName,
        j_code: (
          getItemFromLocalStorage(
            `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
          ) ?? []
        ).join(","),
        assigned_primary_icd_code: primaryIcdCodes,
        jcode_icd,
      },
    });
    dispatch(setOpenedModalId({ id: ModalId.CLINICAL_DATA_TABLE }));
  };
  const handleViewGuidelines = () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.VIEW_GUIDELINES_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        payer_name: payerName,
      },
    });
    handleViewNccnGuidelines(dispatch, props.items, {
      event_name: EventsName.VIEW_NCCN_GUIDELINES_CLICKED,
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        payer_name: payerName,
      },
    });
  };
  const handleViewTxPlan = () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.VIEW_TX_PLAN_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        payer_name: payerName,
      },
    });
    dispatch(setOpenedModalId({ id: ModalId.IMAGE_VIEWER_MODAL }));
  };

  const handleViewNarGrid = async () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.VIEW_NAR_CLIKCED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        payer_name: payerName,
      },
    });
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    let foundSource = "";
    const createdAtDates: string[] = [];

    singleMedicalPaOrder?.auth_on_file?.auth_entries?.some((entry) =>
      entry.j_codes?.some((jcode) => {
        if (jcode.nar_status?.auth_grid?.created_at) {
          createdAtDates.push(jcode.nar_status.auth_grid.created_at);
        }
        if (jcode.nar_status?.auth_grid?.source) {
          foundSource = jcode.nar_status.auth_grid.source;
          return true;
        }
        return false;
      }),
    );

    // Find the latest created_at
    const latestCreatedAt =
      createdAtDates.length > 0
        ? createdAtDates.sort(
            (a, b) => new Date(b).getTime() - new Date(a).getTime(),
          )[0]
        : "";

    if (!foundSource) {
      dispatch(
        setOpenedModalId({
          id: ModalId.NAR_GRID_MODAL,
          metaData: {
            source: "",
            loading: true,
            created_at: latestCreatedAt,
          },
        }),
      );
      try {
        const authGridSource = await getAuthGridSource({
          order_id: singleMedicalPaOrder?.order_id ?? "",
          signal: signal,
        });
        dispatch(
          setMetaData({
            source: authGridSource.source ?? "",
            loading: false,
            created_at: latestCreatedAt,
          }),
        );
        // Clear the abort controller reference on success
        abortControllerRef.current = null;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          dispatch(closeModal());
        } else {
          dispatch(
            setMetaData({
              source: "",
              loading: false,
              created_at: latestCreatedAt,
            }),
          );
          controlToastState("nar-grid-not-available");
          console.error("Error getting auth grid source", error);
        }
        // Clear the abort controller reference on error
        abortControllerRef.current = null;
        return;
      }
    } else {
      dispatch(
        setOpenedModalId({
          id: ModalId.NAR_GRID_MODAL,
          metaData: {
            source: foundSource,
            loading: false,
            created_at: latestCreatedAt,
          },
        }),
      );
    }
  };

  const handleMouseEnter = () => {
    setIsOpen(true);
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.ORDER_MENU_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        payer_name: payerName,
      },
    });
  };

  return (
    <div
      className="three-dots-with-clickable-options--container flex h-[22px] w-[22px] items-center justify-center bg-[#F7F9FA] hover:scale-105 hover:cursor-pointer"
      id="medical-pa-form"
      onMouseEnter={handleMouseEnter}
      ref={parentRef}
    >
      <ThreeDotIcon />
      <AnchoredPortal
        parentRef={parentRef}
        shouldShow={isOpen}
        top={position.bottom + 5}
        right={15}
        zIndex={1000}
        children={
          <div onMouseLeave={() => setIsOpen(false)}>
            <PopOverWithClickableOptions
              options={
                shouldShowViewGuidelinesOption
                  ? [
                      {
                        text: "View Guidelines",
                        id: optionsEnum.VIEW_GUIDELINES,
                      },
                      { text: "View Tx Plan", id: optionsEnum.VIEW_TX_PLAN },
                      {
                        text: "View in Onco EMR",
                        id: optionsEnum.VIEW_IN_ONCO_EMR,
                      },
                      { text: "View DX Codes", id: optionsEnum.VIEW_DX_CODES },
                      { text: "View NAR Grid", id: optionsEnum.VIEW_NAR_GRID },
                    ]
                  : [
                      { text: "View Tx Plan", id: optionsEnum.VIEW_TX_PLAN },
                      {
                        text: "View in Onco EMR",
                        id: optionsEnum.VIEW_IN_ONCO_EMR,
                      },
                      { text: "View DX Codes", id: optionsEnum.VIEW_DX_CODES },
                      { text: "View NAR Grid", id: optionsEnum.VIEW_NAR_GRID },
                    ]
              }
              onClick={handleOnOptionsClick}
            />
          </div>
        }
      />
      {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
        <DocViewerWithSideNavigation
          screenName={ScreenNamesForBigQuery.FORM_SCREEN}
          shouldLogEvent={true}
          modalName={ModalNamesForBigQuery.NCCN_GUIDELINES_MODAL}
        />
      )}
      {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
        <ImageviewerModal
          fileUrls={[]}
          onClose={() => {
            dispatch(closeModal());
          }}
          filePaths={
            singleMedicalPaOrder?.auth_on_file?.screenshots?.treatment_plan_path
              ? [
                  singleMedicalPaOrder?.auth_on_file?.screenshots
                    ?.treatment_plan_path,
                ]
              : []
          }
        >
          <ImageViewerTimestamp
            createdAt={
              singleMedicalPaOrder?.auth_on_file?.screenshots?.created_at
            }
          />
        </ImageviewerModal>
      )}
      {openedModalId === ModalId.NAR_GRID_MODAL && (
        <NarGridModal
          onClose={() => {
            dispatch(closeModal());
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
            abortControllerRef.current = null;
          }}
        />
      )}
    </div>
  );
};

export default ThreeDotsWithClickableOptions;
